import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Phone, MessageCircle, Mail, Instagram, Trash2, Plus, Lock,
  Briefcase, Sparkles, Scissors, Users,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// VendorsMobileView (Feb 2026 Partner & Fornitori sprint)
//
// Mobile-only redesign of the Partner & Fornitori tab. Rendered inside
// an `md:hidden` wrapper from ClientDetail.jsx; desktop keeps the
// original VendorsSection layout unchanged.
//
// HONESTY NOTE — what we CAN and CAN'T do with zero data-model change:
//
//   ✅ Wedding Planner — already has contact_name, phone, email, agency_name,
//      commercial_agreement.* in client.wedding_planner → full CTA card.
//   ✅ Team Beauty — names + colors + phone/email come from the `team`
//      collection loaded by the parent; we render MUA / Hair / Assistants
//      from client.makeup_artist_id, hair_artist_id, assistant_ids[].
//   ⚠️ Vendors — data model only stores name/instagram/notes (no phone/
//      email). So vendor cards keep the same fields the desktop has,
//      reorganized for touch; tap on Instagram handle opens the profile
//      in the IG app/web. Adding phone/email to vendors would require
//      a backend change, which is OUT OF SCOPE for this sprint.
//   ⚠️ Alerts — same reason: no "vendor senza telefono" alert is possible
//      because no telefono field exists. We surface only the alerts that
//      can be derived from EXISTING fields (WP missing, vendor name empty
//      but row exists, etc.).
//
// All callbacks (`patch`, slot helpers) are passed in as props. Zero
// new business logic.
// ─────────────────────────────────────────────────────────────────────────────

function _cleanPhone(raw) { return raw ? String(raw).replace(/[^\d+]/g, "") : ""; }
function _telHref(raw) { const p = _cleanPhone(raw); return p ? `tel:${p}` : ""; }
function _waHref(raw, msg) {
  const p = _cleanPhone(raw).replace(/^\+/, "");
  if (!p) return "";
  return msg ? `https://wa.me/${p}?text=${encodeURIComponent(msg)}` : `https://wa.me/${p}`;
}
function _instaUrl(handle) {
  if (!handle) return "";
  const h = String(handle).replace(/^@+/, "").trim();
  return h ? `https://instagram.com/${h}` : "";
}

// Tap-action button used inside cards. The compact variant is for cards
// with limited width (e.g. fornitori); the full variant is for the
// primary Wedding Planner card.
function TapAction({ icon: Icon, label, href, target, color, testid, compact = false }) {
  const enabled = !!href;
  const baseHeight = compact ? "h-10" : "h-11";
  const colors = {
    emerald: enabled ? "bg-emerald-50 border-emerald-200 text-emerald-800 active:bg-emerald-100" : "",
    green: enabled ? "bg-green-50 border-green-200 text-green-800 active:bg-green-100" : "",
    blue: enabled ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100" : "",
    pink: enabled ? "bg-pink-50 border-pink-200 text-pink-800 active:bg-pink-100" : "",
  };
  const cls = enabled ? colors[color] : "bg-neutral-50 border-neutral-200 text-neutral-300";
  return (
    <a
      href={href || undefined}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      onClick={(e) => { if (!enabled) e.preventDefault(); }}
      aria-disabled={!enabled}
      className={`flex items-center justify-center gap-1.5 ${baseHeight} rounded-md border transition-colors ${cls}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" strokeWidth={1.7} />
      <span className="text-[11.5px] font-medium uppercase tracking-wide">{label}</span>
    </a>
  );
}

// ─── Wedding Planner Card (mobile) ───────────────────────────────────────
function WeddingPlannerCardMobile({ wp = {} }) {
  const contactName = wp.contact_name || "";
  const agency = wp.agency_name || "";
  const phone = wp.phone || "";
  const email = wp.email || "";
  const status = wp.status || "draft";
  const agreement = wp.commercial_agreement || {};
  const fee = agreement.value || 0;
  const feeType = agreement.type || "none";
  const referral = !!agreement.referral_active;

  const isEmpty = !contactName && !agency && !phone && !email;
  const statusLabel = {
    draft: "Bozza",
    proposed: "Proposto",
    confirmed: "Confermato",
    declined: "Rifiutato",
  }[status] || status;
  const statusTone = status === "confirmed"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "proposed"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : status === "declined"
        ? "bg-neutral-50 text-neutral-500 border-neutral-200"
        : "bg-neutral-50 text-neutral-600 border-neutral-200";

  return (
    <section
      className="rounded-md border bg-gradient-to-br from-[#FFFAF0] to-white border-[#C8A97E]/40 p-4"
      data-testid="vendors-mobile-wp"
    >
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Briefcase className="w-4 h-4 text-[#7B6A45] shrink-0" strokeWidth={1.5} />
          <span className="label-uppercase text-[#7B6A45]">Wedding Planner</span>
        </div>
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusTone}`}
          data-testid="vendors-mobile-wp-status"
        >
          {statusLabel}
        </span>
      </header>

      {isEmpty ? (
        <div className="text-sm text-neutral-500 italic py-2" data-testid="vendors-mobile-wp-empty">
          Nessun wedding planner assegnato.
          Tocca <strong>Modifica</strong> sotto per aggiungere i dati.
        </div>
      ) : (
        <>
          <div className="space-y-1.5 mb-3">
            {contactName && (
              <div className="text-base font-medium text-neutral-900">{contactName}</div>
            )}
            {agency && (
              <div className="text-[12.5px] text-neutral-500">{agency}</div>
            )}
            {phone && (
              <div className="text-[12.5px] text-neutral-600 tabular-nums">{phone}</div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <TapAction icon={Phone} label="Chiama" href={_telHref(phone)} color="emerald" testid="vendors-mobile-wp-call" />
            <TapAction
              icon={MessageCircle}
              label="WhatsApp"
              href={_waHref(phone, contactName ? `Ciao ${contactName}, ti scrivo per il matrimonio.` : "")}
              target="_blank"
              color="green"
              testid="vendors-mobile-wp-whatsapp"
            />
            <TapAction
              icon={Mail}
              label="Email"
              href={email ? `mailto:${email}` : ""}
              color="blue"
              testid="vendors-mobile-wp-email"
            />
          </div>

          {(fee > 0 || referral || feeType !== "none") && (
            <div className="border-t border-[#C8A97E]/20 pt-3 space-y-1 text-[12.5px]" data-testid="vendors-mobile-wp-agreement">
              {feeType !== "none" && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Fee:</span>
                  <span className="font-medium text-neutral-800 tabular-nums">
                    € {Number(fee || 0).toLocaleString("it-IT")}
                    {feeType === "percentage" ? " %" : feeType === "per_event" ? " (forfait)" : ""}
                  </span>
                </div>
              )}
              {referral && (
                <div className="flex justify-between">
                  <span className="text-neutral-500">Referral:</span>
                  <span className="font-medium text-pink-700">Attivo</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ─── Team Beauty Card (mobile) ───────────────────────────────────────────
function TeamBeautyCardMobile({ client, team }) {
  const teamById = React.useMemo(() => {
    const m = new Map();
    (team || []).forEach((t) => m.set(t.id, t));
    return m;
  }, [team]);

  const mua = teamById.get(client.makeup_artist_id);
  const hair = teamById.get(client.hair_artist_id);
  const assistants = (client.assistant_ids || []).map((id) => teamById.get(id)).filter(Boolean);

  const all = [
    mua && { ...mua, role: "Make-up Artist", icon: Sparkles, key: `mua-${mua.id}` },
    hair && { ...hair, role: "Hairstylist", icon: Scissors, key: `hair-${hair.id}` },
    ...assistants.map((a) => ({ ...a, role: "Assistente", icon: Users, key: `ass-${a.id}` })),
  ].filter(Boolean);

  if (all.length === 0) return null;

  return (
    <section className="rounded-md border border-border bg-white p-4" data-testid="vendors-mobile-team">
      <header className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
        <span className="label-uppercase">Team Beauty</span>
        <span className="ml-auto text-[11px] text-neutral-500 tabular-nums">{all.length}</span>
      </header>
      <ul className="divide-y divide-border" data-testid="vendors-mobile-team-list">
        {all.map((m) => {
          const Icon = m.icon;
          return (
            <li
              key={m.key}
              className="py-3 first:pt-0 last:pb-0 flex items-center gap-3"
              data-testid={`vendors-mobile-team-${m.id}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: m.color || "#94A3B8" }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-neutral-900 truncate flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 text-neutral-400 shrink-0" strokeWidth={1.5} />
                  {m.name}
                </div>
                <div className="text-[11px] text-neutral-500">{m.role}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <a
                  href={_telHref(m.phone) || undefined}
                  onClick={(e) => { if (!m.phone) e.preventDefault(); }}
                  aria-disabled={!m.phone}
                  className={`w-9 h-9 rounded-md border flex items-center justify-center ${
                    m.phone
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700 active:bg-emerald-100"
                      : "bg-neutral-50 border-neutral-200 text-neutral-300"
                  }`}
                  data-testid={`vendors-mobile-team-${m.id}-call`}
                  aria-label="Chiama"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.7} />
                </a>
                <a
                  href={_waHref(m.phone) || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!m.phone) e.preventDefault(); }}
                  aria-disabled={!m.phone}
                  className={`w-9 h-9 rounded-md border flex items-center justify-center ${
                    m.phone
                      ? "bg-green-50 border-green-200 text-green-700 active:bg-green-100"
                      : "bg-neutral-50 border-neutral-200 text-neutral-300"
                  }`}
                  data-testid={`vendors-mobile-team-${m.id}-whatsapp`}
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.7} />
                </a>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Vendor Row (mobile, compact). Same fields as desktop (name, instagram,
//   notes) — no phone/email since the data model doesn't store them. ──
function VendorRowMobile({
  testid, label, name, instagram, notes,
  onChangeName, onChangeInstagram, onChangeNotes, onRemove,
  customLabelEditable = false, labelValue = "", onChangeLabel,
}) {
  const isFilled = (name && name.trim()) || (instagram && instagram.trim());
  const igUrl = _instaUrl(instagram);
  return (
    <div
      className={`rounded-md border p-3 ${isFilled ? "bg-white" : "bg-neutral-50/60 border-dashed"}`}
      style={isFilled ? { backgroundColor: "#FFFDFA" } : undefined}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        {customLabelEditable ? (
          <Input
            value={labelValue}
            onChange={(e) => onChangeLabel?.(e.target.value)}
            placeholder="Tipologia (es. Cake Designer)"
            className="font-serif-heading text-base h-9 rounded-sm flex-1"
            data-testid={`${testid}-custom-label`}
          />
        ) : (
          <h4 className="font-serif-heading text-base">{label}</h4>
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm text-neutral-400 hover:text-red-600 h-8 w-8 p-0 shrink-0"
            onClick={onRemove}
            data-testid={`${testid}-remove`}
            aria-label="Rimuovi"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>
      <div className="space-y-2">
        <div>
          <Label className="label-uppercase text-[10px]">Nome fornitore</Label>
          <Input
            value={name}
            onChange={(e) => onChangeName(e.target.value)}
            placeholder="Es. Studio Bianchi"
            className="mt-1 rounded-sm h-10"
            data-testid={`${testid}-name`}
          />
        </div>
        <div>
          <Label className="label-uppercase text-[10px] flex items-center gap-1.5">
            <Instagram className="w-3 h-3" strokeWidth={1.5} /> Instagram
          </Label>
          <div className="flex gap-1.5 mt-1">
            <Input
              value={instagram}
              onChange={(e) => onChangeInstagram(e.target.value)}
              placeholder="@nomeprofilo"
              className="rounded-sm h-10 flex-1"
              data-testid={`${testid}-instagram`}
            />
            <a
              href={igUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (!igUrl) e.preventDefault(); }}
              aria-disabled={!igUrl}
              className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${
                igUrl
                  ? "bg-pink-50 border-pink-200 text-pink-700 active:bg-pink-100"
                  : "bg-neutral-50 border-neutral-200 text-neutral-300"
              }`}
              data-testid={`${testid}-instagram-open`}
              aria-label="Apri Instagram"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.7} />
            </a>
          </div>
        </div>
        {(notes || isFilled) && (
          <div>
            <Label className="label-uppercase text-[10px]">Note</Label>
            <Input
              value={notes}
              onChange={(e) => onChangeNotes(e.target.value)}
              placeholder="Es. da contattare entro Maggio…"
              className="mt-1 rounded-sm h-10"
              data-testid={`${testid}-notes`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function VendorsMobileView({
  client,
  team,
  VENDOR_SLOTS,
  getSlot,
  updateSlot,
  others,
  updateOther,
  addOther,
  removeOther,
}) {
  return (
    <div className="space-y-4 pb-6" data-testid="vendors-mobile-view">
      {/* Hero header */}
      <header className="space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-serif-heading text-xl">Partner & Fornitori</h2>
          <span
            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
            style={{ backgroundColor: "rgba(200,169,126,0.12)", color: "#7B6A45", border: "1px solid rgba(200,169,126,0.4)" }}
            data-testid="vendors-mobile-internal-badge"
          >
            <Lock className="w-2.5 h-2.5" strokeWidth={1.5} /> Solo uso interno
          </span>
        </div>
        <p className="text-[11.5px] text-neutral-500 leading-relaxed">
          Wedding Planner, team beauty e fornitori del matrimonio. Mai esposto a sposa o PDF.
        </p>
      </header>

      {/* 1. WEDDING PLANNER */}
      <WeddingPlannerCardMobile wp={client.wedding_planner || {}} />

      {/* 2. TEAM BEAUTY (read-only — assegnazione team si fa nella tab Wedding) */}
      <TeamBeautyCardMobile client={client} team={team} />

      {/* 3. FORNITORI principali */}
      <section className="space-y-2.5" data-testid="vendors-mobile-predefined">
        <h3 className="font-serif-heading text-base px-1">Fornitori principali</h3>
        {VENDOR_SLOTS.map((slot) => {
          const row = getSlot(slot.slug) || { name: "", instagram: "", notes: "" };
          return (
            <VendorRowMobile
              key={slot.slug}
              label={slot.label}
              testid={`vendors-mobile-row-${slot.slug}`}
              name={row.name || ""}
              instagram={row.instagram || ""}
              notes={row.notes || ""}
              onChangeName={(v) => updateSlot(slot.slug, "name", v)}
              onChangeInstagram={(v) => updateSlot(slot.slug, "instagram", v)}
              onChangeNotes={(v) => updateSlot(slot.slug, "notes", v)}
            />
          );
        })}
      </section>

      {/* 4. ALTRI FORNITORI free-form */}
      <section className="space-y-2.5 pt-2 border-t border-border" data-testid="vendors-mobile-others">
        <div className="flex items-center justify-between gap-2 pt-2 px-1">
          <div>
            <h3 className="font-serif-heading text-base">Altri fornitori</h3>
            <p className="text-[10.5px] text-neutral-500">Cake designer, calligrafo, animazione bambini, ecc.</p>
          </div>
        </div>
        {others.length === 0 ? (
          <p className="text-xs text-neutral-400 italic px-1">Nessun altro fornitore aggiunto.</p>
        ) : (
          others.map(({ v }, i) => (
            <VendorRowMobile
              key={`other-${v.absIdx ?? i}`}
              testid={`vendors-mobile-row-other-${i}`}
              customLabelEditable
              label={v.custom_label || "Tipologia"}
              labelValue={v.custom_label || ""}
              onChangeLabel={(val) => updateOther(i, "custom_label", val)}
              name={v.name || ""}
              instagram={v.instagram || ""}
              notes={v.notes || ""}
              onChangeName={(val) => updateOther(i, "name", val)}
              onChangeInstagram={(val) => updateOther(i, "instagram", val)}
              onChangeNotes={(val) => updateOther(i, "notes", val)}
              onRemove={() => removeOther(i)}
            />
          ))
        )}
        <Button
          variant="outline"
          className="w-full h-11 rounded-sm"
          onClick={addOther}
          data-testid="vendors-mobile-add-other"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Aggiungi fornitore
        </Button>
      </section>
    </div>
  );
}
